import type { ChartSpec, QueryRow } from '../../services/api/dataQa';

import { chartSeries } from './chart-series';

// Varumärkesfärgerna ur tailwind.config.ts; SVG-attribut kan inte ta Tailwind-klasser med opacity.
const SERIES_COLORS = ['#082939', '#044778', '#aea694'];

const WIDTH = 640;
const HEIGHT = 260;
const MARGIN = { top: 12, right: 12, bottom: 48, left: 52 };
const MAX_X_LABELS = 12;
const TICKS = 4;

const numberFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 1 });

// Jämna steg (1, 2, 5 × 10^n) så att axeln inte får värden som 37,5.
const niceStep = (span: number): number => {
  const raw = span / TICKS;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const candidates = [1, 2, 5, 10].map((m) => m * magnitude);
  return candidates.find((c) => c >= raw) ?? candidates[candidates.length - 1];
};

export const ResultChart = ({ spec, rows }: { spec: ChartSpec; rows: QueryRow[] }) => {
  const data = chartSeries(spec, rows);
  if (!data) return null;

  const values = data.series.flatMap((s) => s.values).filter((v): v is number => v !== null);
  const rawMax = Math.max(0, ...values);
  const rawMin = Math.min(0, ...values);
  const step = niceStep(Math.max(rawMax - rawMin, 1));
  const max = Math.ceil(rawMax / step) * step || step;
  const min = Math.floor(rawMin / step) * step;

  const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const yOf = (v: number) => MARGIN.top + plotHeight - ((v - min) / (max - min)) * plotHeight;
  const count = data.labels.length;
  const slot = plotWidth / count;
  const xCenter = (i: number) => MARGIN.left + slot * i + slot / 2;
  const labelEvery = Math.ceil(count / MAX_X_LABELS);
  const rotateLabels = count > 8;

  const ticks: number[] = [];
  for (let v = min; v <= max + step / 2; v += step) ticks.push(v);

  const barWidth = (slot * 0.7) / data.series.length;

  return (
    <figure className="rounded-sm border border-black/10 bg-white p-3">
      <figcaption className="mb-2 text-xs font-semibold text-brand-blue">{spec.title}</figcaption>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={spec.title}
        className="h-auto w-full text-[11px]"
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={MARGIN.left}
              x2={WIDTH - MARGIN.right}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke="#000"
              strokeOpacity={tick === 0 ? 0.3 : 0.08}
            />
            <text x={MARGIN.left - 6} y={yOf(tick) + 4} textAnchor="end" fill="#717171">
              {numberFormat.format(tick)}
            </text>
          </g>
        ))}

        {data.series.map((series, s) =>
          spec.type === 'line' ? (
            <g key={series.name} fill="none" stroke={SERIES_COLORS[s]} strokeWidth={2}>
              <polyline
                points={series.values
                  .map((v, i) => (v === null ? null : `${xCenter(i)},${yOf(v)}`))
                  .filter(Boolean)
                  .join(' ')}
              />
              {series.values.map((v, i) =>
                v === null ? null : (
                  <circle key={i} cx={xCenter(i)} cy={yOf(v)} r={3} fill={SERIES_COLORS[s]}>
                    <title>{`${data.labels[i]}: ${numberFormat.format(v)}`}</title>
                  </circle>
                ),
              )}
            </g>
          ) : (
            <g key={series.name} fill={SERIES_COLORS[s]}>
              {series.values.map((v, i) =>
                v === null ? null : (
                  <rect
                    key={i}
                    x={xCenter(i) - (barWidth * data.series.length) / 2 + barWidth * s}
                    y={Math.min(yOf(v), yOf(0))}
                    width={barWidth}
                    height={Math.max(1, Math.abs(yOf(v) - yOf(0)))}
                  >
                    <title>{`${data.labels[i]}: ${numberFormat.format(v)}`}</title>
                  </rect>
                ),
              )}
            </g>
          ),
        )}

        {data.labels.map((label, i) =>
          i % labelEvery === 0 ? (
            <text
              key={i}
              x={xCenter(i)}
              y={HEIGHT - MARGIN.bottom + 16}
              textAnchor={rotateLabels ? 'end' : 'middle'}
              transform={
                rotateLabels
                  ? `rotate(-35 ${xCenter(i)} ${HEIGHT - MARGIN.bottom + 16})`
                  : undefined
              }
              fill="#717171"
            >
              {label}
            </text>
          ) : null,
        )}
      </svg>
      {data.series.length > 1 ? (
        <ul className="mt-1 flex flex-wrap gap-3 text-[11px] text-text-grey">
          {data.series.map((series, s) => (
            <li key={series.name} className="flex items-center gap-1">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: SERIES_COLORS[s] }}
              />
              {series.name}
            </li>
          ))}
        </ul>
      ) : null}
    </figure>
  );
};
